import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, Table, Pagination, Breadcrumb, Icon } from "common-components";
import { useMachine } from "@xstate/react";
import { fetchMachine, FetchEventType } from "machines/fetchMachine";
import { setSelectOptions } from "utils/common";
import { versionList, subjectsObj, EDUCATIONAL_LIST } from "constants/index";
import { UiTop, UiActionBox, UiTableTop } from "styles";
import { StyledTableItem, UiNewChapterPage } from "./NewChapterPage.style";
import { format } from "date-fns";
import openNotificationWithIcon from "utils/hooks/useNotification";
import useOptions from "./useOptions";
import UploadFilesModal from "./UploadFilesModal/UploadFilesModal";
import { CURRICULUM_OPTIONS, DATA_TYPE, STATUS, statusMapper, VERSION_OPTIONS } from "./constants";
import { deleteChapterRecord, getChapterRecords } from "api/chapter/index";

const { Header, Body, Row } = Table;

const BREADCRUMBS = [
	{
		icon: "home",
		name: "首頁",
		path: "/",
	},
	{
		icon: "list_alt",
		name: `新增版本章節`,
		path: `/newChapter`,
	},
];

/**
 * 目前只有課本章節可以選擇
 */
const DATA_TYPE_OPTIONS = setSelectOptions([
	{ code: "", name: "所有資料類型" },
	{ code: DATA_TYPE.chapter, name: "章節重點" },
	{ code: DATA_TYPE.book, name: "大考冊次" },
	{ code: DATA_TYPE.topic, name: "主題式無課綱" },
]);

const STATUS_OPTIONS = setSelectOptions(
	[
		{
			code: STATUS.none,
			name: "所有狀態",
		},
	].concat(
		Object.values(STATUS)
			.slice(1)
			.map((item) => ({
				code: item,
				name: statusMapper(item),
			})),
	),
);

const DEFAULT_PAGE_SIZE = 10;

const INITIAL_PAGE = 1;

/**
 * 新增版本章節
 */
export const NewChapterPage = () => {
	const { yearOptions, eduOptions, eduSubjectMap } = useOptions();
	const [filters, setFilters] = useState({
		pagenumber: INITIAL_PAGE,
		pagesize: DEFAULT_PAGE_SIZE,
		dataType: "",
		curriculum: "",
		year: "",
		version: "",
		edu: "",
		subject: "",
		status: STATUS.none,
	});
	const [isUploadFilesModalVisible, setIsUploadFilesModalVisible] = useState(false);

	const subjectOptions = useMemo(
		() => setSelectOptions([{ code: "", name: "所有科目" }].concat(eduSubjectMap?.[filters.edu] || [])),
		[eduSubjectMap, filters.edu],
	);

	const [state, send] = useMachine(fetchMachine, {
		services: {
			fetchData: (_context, event) => getChapterRecords(event.payload),
		},
	});
	const { chapterRecord: chapterRecords, total } = state.context?.result?.data || {};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	};

	const onShowSizeChange = (currentPage, limit) => {
		handleFilterChange("pagenumber", currentPage);
		handleFilterChange("pagesize", limit);
	};

	const handleSubmit = (overwritePayload = {}) => {
		send(FetchEventType.Fetch, { payload: { ...filters, ...overwritePayload } });
	};

	const downloadFile = (fileUrl) => {
		window.open(fileUrl);
	};

	const deleteRecord = async (uid) => {
		await deleteChapterRecord({ UIDs: [uid] });
		openNotificationWithIcon("success", "刪除成功！");
		handleSubmit();
	};

	useEffect(() => {
		if (!yearOptions?.length) return;
		const year = yearOptions[yearOptions.length - 1].value;
		handleFilterChange("year", year);
		handleSubmit({ year });
	}, [yearOptions]);

	return (
		<UiNewChapterPage>
			<UiTableTop>
				<div className="breadcrumb">
					<Breadcrumb data={BREADCRUMBS} />
				</div>
				<UiTop>
					<UiActionBox>
						<Select
							options={DATA_TYPE_OPTIONS}
							value={filters.dataType}
							onChange={(value) => handleFilterChange("dataType", value ?? "")}
						/>
						<Select
							options={CURRICULUM_OPTIONS}
							value={filters.curriculum}
							onChange={(value) => handleFilterChange("curriculum", value ?? "")}
						/>
						<Select
							options={yearOptions}
							value={filters.year}
							onChange={(value) => handleFilterChange("year", value ?? "")}
						/>
						<Select
							options={VERSION_OPTIONS}
							value={filters.version}
							onChange={(value) => handleFilterChange("version", value ?? "")}
						/>
						<Select
							options={eduOptions}
							value={filters.edu}
							onChange={(value) => handleFilterChange("edu", value ?? "")}
						/>
						<Select
							options={subjectOptions}
							value={filters.subject}
							onChange={(value) => handleFilterChange("subject", value ?? "")}
						/>
						<Select
							options={STATUS_OPTIONS}
							value={filters.status}
							onChange={(value) => handleFilterChange("status", value ?? "")}
						/>
						<Button.IconButton
							variant="blue"
							full={false}
							iconName="search"
							onClick={() => {
								handleFilterChange("pagenumber", INITIAL_PAGE);
								handleSubmit({ pagenumber: INITIAL_PAGE });
							}}>
							查詢
						</Button.IconButton>
					</UiActionBox>
					<div style={{ marginLeft: "auto" }}>
						<Button.IconButton
							variant="blue"
							full={false}
							iconName="cloud_upload"
							onClick={() => setIsUploadFilesModalVisible(true)}>
							上傳檔案
						</Button.IconButton>
					</div>
				</UiTop>
			</UiTableTop>
			<Table margin={20}>
				<Header>
					<Row>
						<StyledTableItem>資料類型</StyledTableItem>
						<StyledTableItem>課綱</StyledTableItem>
						<StyledTableItem>學年度</StyledTableItem>
						<StyledTableItem>版本</StyledTableItem>
						<StyledTableItem>學制</StyledTableItem>
						<StyledTableItem>科目</StyledTableItem>
						<StyledTableItem flex={1}>檔案名稱</StyledTableItem>
						<StyledTableItem flex={1}>上傳時間</StyledTableItem>
						<StyledTableItem flex={0.5}>狀態</StyledTableItem>
						<StyledTableItem flex={1}>操作</StyledTableItem>
					</Row>
				</Header>
				<Body>
					{(chapterRecords || []).map((item) => (
						<Row key={item.uid}>
							<StyledTableItem>{item.dataType}</StyledTableItem>
							<StyledTableItem>{`${item.curriculum}課綱`}</StyledTableItem>
							<StyledTableItem>{item.year}</StyledTableItem>
							<StyledTableItem>{versionList?.[item.version] || item.version}</StyledTableItem>
							<StyledTableItem>{EDUCATIONAL_LIST?.[item.edu] || item.edu}</StyledTableItem>
							<StyledTableItem>{subjectsObj?.[item.subject] || item.subject}</StyledTableItem>
							<StyledTableItem flex={1}>{item.fileName}</StyledTableItem>
							<StyledTableItem flex={1}>{format(new Date(item.createTime), "yyyy-MM-dd HH:mm")}</StyledTableItem>
							<StyledTableItem flex={0.5}>{statusMapper(item.status)}</StyledTableItem>
							<StyledTableItem flex={1}>
								<div className="actions">
									<Button.IconButton
										iconName="file_download"
										full={false}
										textColor="black"
										type="outLined"
										onClick={() => downloadFile(item.fileUrl)}>
										下載
									</Button.IconButton>
									<Button.Popconfirm
										title="確定要刪除？"
										variant="red"
										full={false}
										onConfirm={() => deleteRecord(item.uid)}>
										<Icon name="delete" mr="2" />
										刪除
									</Button.Popconfirm>
								</div>
							</StyledTableItem>
						</Row>
					))}
				</Body>
			</Table>
			{total > DEFAULT_PAGE_SIZE && (
				<Pagination
					defaultCurrent={INITIAL_PAGE}
					current={filters.pagenumber}
					onChange={(value) => {
						handleFilterChange("pagenumber", value);
						handleSubmit({ pagenumber: value });
					}}
					onShowSizeChange={onShowSizeChange}
					total={total}
					defaultPageSize={DEFAULT_PAGE_SIZE}
				/>
			)}
			<UploadFilesModal
				isVisible={isUploadFilesModalVisible}
				setIsVisible={setIsUploadFilesModalVisible}
				refetch={() => {
					handleFilterChange("pagenumber", INITIAL_PAGE);
					handleSubmit({ pagenumber: INITIAL_PAGE });
				}}
			/>
		</UiNewChapterPage>
	);
};
